<!--
// app/components/Shared/slider-matches/SliderMatchesTurnMatch.vue
-->
<script setup lang="ts">
import type { Team } from "~/src/team/domain/models/Team";
import type { Match } from "~/src/match/domain/models/Match";
import type { SocialMedia } from "~/src/region/domain/models/Region";
import { setAliasUrl, slugify } from "~/src/Shared/utils";
import { useRegions } from "~/app/composables/regions";
import { useMatches } from "~/app/composables/season/matches";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useWebContext } from "~/app/composables/webContext";
import matchUiService from "~/src/match/infrastructure/ui-services/matchUiService";
import matchService from "~/src/match/domain/services/matchService";
import nuxtLink from "#app/components/nuxt-link";
import RegionUiService from "~/src/region/infrastructure/ui-services/regionUiService";
import { $MATCH_REFRESH_INTERVAL_SLIDE } from "~/src/match/domain/services/matchService";

type Props = {
  match: Match;
  teams: Record<number, Team>;
  showGroup: boolean;
  regionReference?: number;
};

const props = defineProps<Props>();
const { locale } = useI18n();
const localePath = useLocalePath();
const { region } = useCompetitionStore();
const { regionParam, regionTimeZone, isWorldCup, isRegionHome } = useRegions();
const { findRegionInfoByRegionId } = RegionUiService();
const { showTime, showDate, setMatchTimeZone } = useMatches();
const { matchHasPage, matchIsLive } = matchService();
const { transformStatus } = matchUiService();
const { webContext } = useWebContext();
const intervalId = ref<NodeJS.Timeout>();

const matchData = ref<Match | undefined>(props.match);

const matchMinute = ref<Match["currentMinute"] | undefined>(props.match.currentMinute);

const setLive = computed(() => {
  return matchIsLive(props.match);
});

const regionParamUsed: string = props.regionReference
  ? findRegionInfoByRegionId(props.regionReference, "slug")
  : regionParam
    ? regionParam
    : "espana";

const regionTimeUsed = props.regionReference
  ? findRegionInfoByRegionId(props.regionReference, "timeZone")
  : regionTimeZone;

const forcedTimezone: boolean = !!props.match.metaInformation && !!props.match.metaInformation.match_timezone;

const showPenalties = computed(() => {
  if (!props.match || !props.match.status) return false;
  else if (props.match.status === "inPlayP" || transformStatus(props.match.status) === "ended") {
    return true;
  }
});

const socialMedia: SocialMedia[] = region?.social || [];
const liveButtonUrl: string | undefined =
  isWorldCup && locale.value === "es"
    ? "https://www.twitch.tv/kingsleague"
    : socialMedia.find(s => s.name === "twitch")?.url;

const clearMatchInterval = () => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
    intervalId.value = undefined;
  }
};

const {
  data: resultsData,
  error: resultsError,
  execute,
} = useFetch(`/api/v1/competition/matches/${props.match.id}/results`, {
  immediate: false,
});

const updateMatchResults = async () => {
  await execute();
  if (resultsError.value) console.error(resultsError.value);
  if (resultsData.value) {
    matchMinute.value = resultsData.value.currentMinute;
    matchData.value["scores"] = resultsData.value.scores;
  }
};

onMounted(() => {
  if (props.match && props.match.status && matchIsLive(props.match)) {
    intervalId.value = setInterval(async function () {
      if (matchData.value) updateMatchResults();
      if (matchData.value && !matchIsLive(props.match)) {
        clearMatchInterval();
      }
    }, $MATCH_REFRESH_INTERVAL_SLIDE);
  }
});

onUnmounted(() => {
  clearMatchInterval();
});

const timeZoneInfo = setMatchTimeZone(
  forcedTimezone ? "appendix" : "stadium",
  forcedTimezone ? props.match.metaInformation?.match_timezone : props.match.stadiumId
);
const computedTimeZone = timeZoneInfo?.timezone || regionTimeUsed;
const timeZoneAppendix = timeZoneInfo?.appendix || "";

const showTimeComputed = showTime(props.match.date);
const showDateComputed = showDate(props.match.date);
</script>

<template>
  <div
    :id="match.id"
    class="card-match"
    :class="[
      { 'bg-gray-900 live-match': setLive },
      { 'border border-green-500': match.groupName?.toLowerCase() === 'challenger' },
    ]"
    :data-last-played="match.isLastPlayed"
  >
    <div v-if="match.id !== 1900" class="match-info-wrapper">
      <div v-if="setLive" class="flex justify-between items-center gap-x-2">
        <span v-if="matchMinute || matchMinute === 0" class="minute-live-match">{{ matchMinute }}'</span>
        <a :href="liveButtonUrl" target="_blank" class="is-live-box">
          <span class="bg-red-600 w-2 h-2 rounded-full animate-pulse" />
          {{ $t("slider.matches.seeLive") }}
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M3.8 4H8M8 4V8.2M8 4L3 9"
              stroke="#F98080"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </a>
      </div>

      <div v-else-if="match.date" class="match-hour">
        <NuxtTime
          v-if="showDateComputed"
          :datetime="match.date"
          :locale="locale"
          :time-zone="computedTimeZone"
          day="numeric"
          month="2-digit"
        />
        <span v-if="showDateComputed && showTimeComputed"> - </span>
        <NuxtTime
          v-if="showTimeComputed"
          :datetime="match.date"
          :locale="locale"
          :time-zone="computedTimeZone"
          hour="2-digit"
          minute="2-digit"
        />
        <span v-if="timeZoneAppendix && showTimeComputed" class="ps-1">{{ timeZoneAppendix }}</span>
      </div>

      <div v-if="match.groupName && showGroup" class="match-group">
        {{ match.groupName?.toLowerCase() === "challenger" ? "chl" : match.groupName }}
      </div>
    </div>
    <nuxt-link
      v-if="match.id === 1900"
      :to="localePath({ name: 'FinalissimaQueens' })"
      external
      class="h-full max-h-[157px] flex rounded-lg bg-[url('/img/landings/bg-finalissima-top.jpg')] bg-right-top bg-no-repeat bg-cover text-center"
    >
      <nuxt-img
        src="/img/league-logos/queens_finalissima.png"
        alt="Queens Finalissima Paris 2025"
        width="568"
        height="720"
        quality="90"
        fit="outside"
        class="w-24 py-[9.35px] mx-auto"
      />
    </nuxt-link>
    <component
      :is="matchHasPage(match.seasonId) ? nuxtLink : 'div'"
      v-else
      :to="
        matchHasPage(match.seasonId)
          ? localePath({
              name: 'region-matches-matchId-homeTeam-vs-awayTeam',
              params: {
                region: regionParamUsed,
                matchId: match.id,
                homeTeam: slugify(teams[match.participants.homeTeamId].shortName || ''),
                awayTeam: slugify(teams[match.participants.awayTeamId].shortName || ''),
              },
            })
          : undefined
      "
      class="match-data-wrapper"
      :no-prefetch="nuxtLink && !setLive && isRegionHome ? true : undefined"
    >
      <div class="team-data-wrapper">
        <div class="team-short-name">
          {{ teams[match.participants.homeTeamId].shortName }}
        </div>
        <div class="team-logo-wrapper">
          <img
            v-if="
              match.metaInformation?.extra_card &&
              match.metaInformation?.extra_card
                .split(',')
                .some(id => id === match.participants.homeTeamId.toString())
            "
            :src="`/img/secret-card/extra-card-${webContext}.png`"
            alt="Secret Card"
            class="w-4 h-auto absolute ltr:right-0 rtl:left-0"
          />
          <NuxtImg
            :src="setAliasUrl(teams[match.participants.homeTeamId].logo.url)"
            :alt="teams[match.participants.homeTeamId].completeName || ''"
            class="max-w-10 h-10"
            height="40"
            width="40"
            loading="lazy"
          />
        </div>
      </div>
      <div class="home-team-result-wrapper">
        <div class="flex justify-center items-center h-full">
          <div>
            {{ matchData?.scores.homeScore === null ? "-" : matchData?.scores.homeScore }}
            <span v-if="showPenalties" class="text-primary text-xs">{{ matchData?.scores.homeScoreP }}</span>
          </div>
        </div>
      </div>
      <div class="team-data-wrapper">
        <div class="team-short-name">
          {{ teams[match.participants.awayTeamId].shortName }}
        </div>
        <div class="team-logo-wrapper">
          <img
            v-if="
              match.metaInformation?.extra_card &&
              match.metaInformation?.extra_card
                .split(',')
                .some(id => id === match.participants.awayTeamId.toString())
            "
            :src="`/img/secret-card/extra-card-${webContext}.png`"
            alt="Secret Card"
            class="w-4 h-auto absolute ltr:right-0 rtl:left-0"
          />
          <NuxtImg
            :src="setAliasUrl(teams[match.participants.awayTeamId].logo.url)"
            :alt="teams[match.participants.awayTeamId].completeName || ''"
            :loading="setLive ? 'eager' : 'lazy'"
            class="max-w-10 h-10"
            height="40"
            width="40"
          />
        </div>
      </div>
      <div class="away-team-result-wrapper">
        <div class="flex justify-center items-center h-full">
          <div>
            {{ matchData?.scores.awayScore === null ? "-" : matchData?.scores.awayScore }}
            <span v-if="showPenalties" class="text-primary text-xs">{{ matchData?.scores.awayScoreP }}</span>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<style scoped>
.card-match {
  @apply rounded-md min-w-[168px] p-2.5 max-w-full hover:bg-gray-900;
}

.match-info-wrapper {
  @apply flex flex-nowrap justify-between items-center gap-1 pb-2 border-b border-b-gray-600;
}

.is-live-box {
  @apply flex gap-1 items-center text-xs text-red-400 uppercase bg-gray-800 rounded justify-center px-1;
}

.minute-live-match {
  @apply text-gray-500 text-xs font-medium tracking-tight;
}

.match-hour {
  @apply text-xs font-medium text-gray-200;
}

.match-group {
  @apply px-1.5 rounded border border-primary uppercase text-[10px] font-normal text-primary leading-[17px];
}

.match-data-wrapper {
  @apply grid grid-cols-[1fr_1fr_40px] gap-x-1.5 mt-4 text-center;
}

.team-data-wrapper {
  @apply grid grid-cols-[38px_1fr] items-center col-span-2;
}

.team-short-name {
  @apply text-white font-bold text-left;
}

.team-logo-wrapper {
  @apply flex flex-col items-start justify-center py-1 px-1.5 relative;
}

.home-team-result-wrapper {
  @apply bg-gray-800 font-bold text-center rounded-t-md;
}

.away-team-result-wrapper {
  @apply bg-gray-800 font-bold text-center rounded-b-md;
}
</style>
